import { useSnapshot } from 'valtio'
import { state } from '../../../context/store'
import { productChoices } from '../../../context/productChoices'
import { Button, Column, Select, Option, Text } from '../../Blocks'
import { useEffect, useState } from 'react'

const STEPS = [
  {
    key: 'type',
    dependsOn: null,
    heading: 'Pick Your Bike Type',
    id: 'bike-type-select',
    getOptions: ({ choiceSummary }) => choiceSummary,
    isActive: () => true,
    onSelect: (value) => {
      if (state.current.initialChoices.type.index === value.index) return

      state.current.initialChoices.threeDModel = 'null'
      state.current.initialChoices.type = value
      state.current.initialChoices.make = { name: 'Select your Bike Make', index: null }
      state.current.initialChoices.model = {
        name: 'Select your Bike Make First',
        index: null,
      }
      state.current.initialChoices.year = {
        name: 'Select your Bike Make First',
        index: null,
      }
    },
  },

  {
    key: 'make',
    dependsOn: 'type',
    heading: 'Pick Your Bike Manufacturer',
    isActive: ({ snap }) => snap.type.index !== null,
    getOptions: ({ snap, productSnap }) =>
      productSnap.choices[snap.type.index].make.map((choice, index) => ({
        name: choice[0],
        index,
        abrv: choice[1],
      })),
    onSelect: (value) => {
      if (state.current.initialChoices.make.index === value.index) return

      state.current.initialChoices.threeDModel = 'null'
      state.current.initialChoices.make = value
      state.current.initialChoices.model = { name: 'Select your Bike Make', index: null }
      state.current.initialChoices.year = {
        name: 'Select your Bike Make First',
        index: null,
      }
    },
  },

  {
    key: 'model',
    dependsOn: 'make',
    heading: 'Pick Your Bike Model',
    isActive: ({ snap }) => snap.make.index !== null,
    getOptions: ({ snap, productSnap }) =>
      productSnap.choices[snap.type.index].make[snap.make.index][2].map(
        (choice, index) => ({
          name: choice[0],
          index,
        }),
      ),
    onSelect: (value) => {
      if (state.current.initialChoices.model.index === value.index) return

      state.current.initialChoices.threeDModel = 'null'
      state.current.initialChoices.model = value
      state.current.initialChoices.year = { name: 'Select your Bike Year', index: null }
    },
  },

  {
    key: 'year',
    dependsOn: 'model',
    revealOnlyWhenReady: true,
    heading: 'Pick Your Bike Year',
    isActive: ({ snap }) => snap.model.index !== null,
    getOptions: ({ snap, productSnap }) =>
      productSnap.choices[snap.type.index].make[snap.make.index][2][
        snap.model.index
      ][1].map((choice, index) => ({
        name: choice[0],
        index,
      })),
    onSelect: (value, ctx) => {
      if (state.current.initialChoices.year.index === value.index) return

      const modelData =
        ctx.productSnap.choices[ctx.snap.type.index].make[ctx.snap.make.index][2][
        ctx.snap.model.index
        ][1]

      state.current.initialChoices.year = value
      state.current.initialChoices.threeDModel = modelData[value.index][3]
      state.current.initialChoices.inhouseCode = modelData[value.index][1]
      state.current.initialChoices.logoSize = modelData[value.index][4]
      state.current.initialChoices.ribLimit = modelData[value.index][2]
    },
  },
]

const DynamicSelectStep = ({ step, snap, productSnap, choiceSummary }) => {
  const [search, setSearch] = useState('')

  const isDependencyMet = !step.dependsOn || snap[step.dependsOn]?.index !== null

  const allOptions = isDependencyMet
    ? step.getOptions({ snap, productSnap, choiceSummary }) || []
    : []

  const options = search.trim()
    ? allOptions.filter((opt) => opt.name.toLowerCase().includes(search.toLowerCase()))
    : allOptions

  // Reset search when parent dependency changes
  useEffect(() => {
    setSearch('')
  }, [snap[step.dependsOn]?.index])

  // Auto-select single option (guarded)
  useEffect(() => {
    if (
      options.length === 1 &&
      isDependencyMet &&
      snap[step.key]?.index !== options[0].index
    ) {
      step.onSelect(options[0], { snap, productSnap })
    }
  }, [options, isDependencyMet, snap[step.key]?.index])

  // Hide completely until dependency met
  if (step.revealOnlyWhenReady && !isDependencyMet) {
    return null
  }

  // Special case: single year → show compatibility message instead of select
  if (step.key === 'year' && allOptions.length === 1 && isDependencyMet) {
    return (
      <Text size="17px" align="left" format="none">
        This seat is suitable for models made in {allOptions[0].name}.
      </Text>
    )
  }

  // Hide single-option selects (except model, which should always be visible)
  if (allOptions.length === 1 && isDependencyMet && step.key !== 'model') {
    return null
  }

  // Only show search input when there are more than 6 options
  const showSearch = isDependencyMet && allOptions.length > 6

  return (
    <>
      {/* {showSearch && (
        <input
          type="text"
          placeholder={`Search ${step.heading.toLowerCase()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )} */}

      <Select
        stepKey={step.key}
        heading={step.heading}
        id={step.id ?? null}
        selectedLabel={snap[step.key]?.name}
        active={isDependencyMet}
        isDisabled={!isDependencyMet}
        helper={
          !isDependencyMet && !step.revealOnlyWhenReady ? 'Complete the step above' : null
        }
        onChange={(value) => {
          step.onSelect(value, { snap, productSnap })
          setSearch('')
        }}
      >
        {options.map((option) => (
          <Option key={`${step.key}-${option.name}-${option.index}`} data={option} />
        ))}
      </Select>
    </>
  )
}

const ModalSelectModel = () => {
  const snap = useSnapshot(state.current.initialChoices)
  const productSnap = useSnapshot(productChoices.current)

  const choiceSummary = productChoices.current.choices.map((choice) => ({
    name: choice.identifier.name,
    index: choice.identifier.index,
  }))

  useEffect(() => {
    // First remove highlight from all steps
    STEPS.forEach((step) => {
      const el = document.querySelector(`[data-step="${step.key}"]`)
      el?.classList.remove('step-highlight')
    })

    // Then add to the first incomplete
    const firstIncomplete = STEPS.find((step) => {
      const isDependencyMet = !step.dependsOn || snap[step.dependsOn]?.index !== null
      const isSelected = snap[step.key]?.index !== null
      return isDependencyMet && !isSelected
    })

    if (firstIncomplete) {
      const el = document.querySelector(`[data-step="${firstIncomplete.key}"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el?.classList.add('step-highlight')
    }
  }, [snap.type.index, snap.make.index, snap.model.index, snap.year.index])

  return (
    <Column pad="20px" minh={state.current.initialChoices.popupSize} gap="20px">
      <Column gap="20px" justify="flex-start" zindex="100">
        {STEPS.map((step) => (
          <DynamicSelectStep
            key={step.key}
            step={step}
            snap={snap}
            productSnap={productSnap}
            choiceSummary={choiceSummary}
          />
        ))}
      </Column>

      <Button
        accent
        fullWidth
        isDisabled={snap.year.index === null}
        handleClick={() => {
          state.current.modal.selectModel = false
          state.current.modal.disclaimer = true
        }}
      >
        {snap.confirmButtonText}
      </Button>
    </Column>
  )
}

export default ModalSelectModel